/**
 * 字典服務管理器
 * 
 * 管理字典數據的獲取、緩存和更新
 */

import { getDictionaryVersion, getAllDictionaries } from '../api/supabaseApi';
import {
  getCachedVersion,
  getCachedVersionMD5,
  setCachedVersion,
  getCachedDictionary,
  setCachedDictionary,
  getAllCachedDictionaries,
  clearAllCache,
  getLastVersionCheckTimestamp,
  setLastVersionCheckTimestamp
} from '../utils/dictionaryCache';
import { debugLog, validateDictionaryStructure, checkEnvironment } from '../utils/dictionaryDebug';

// Fallback 到本地字典
import MTR_Dict from '../dict/MTR_Dict';
import LRT_Dict from '../dict/LRT_Dict';
import MTRBus_Dict from '../dict/MTRBus_Dict';
import MTRStatus_Dict from '../dict/MTRStatus_Dict';
import TrafficNews_Dict from '../dict/TrafficNews_Dict';
import Immigration_Dict from '../dict/Immigration_Dict';

// 本地字典映射
const LOCAL_DICTIONARIES = {
  MTR: MTR_Dict,
  LRT: LRT_Dict,
  MTRBus: MTRBus_Dict,
  MTRStatus: MTRStatus_Dict,
  TrafficNews: TrafficNews_Dict,
  Immigration: Immigration_Dict
};

// 版本檢查過期時間（毫秒）
// 可由環境變數覆蓋：REACT_APP_DICT_VERSION_CHECK_INTERVAL_MINUTES
// 預設每 12 小時檢查一次版本
const VERSION_CHECK_EXPIRY_TIME = (() => {
  const envValue = parseInt(process.env.REACT_APP_DICT_VERSION_CHECK_INTERVAL_MINUTES || '720', 10);
  if (Number.isFinite(envValue) && envValue > 0) {
    return envValue * 60 * 1000;
  }
  return 60 * 60 * 1000;
})();

/**
 * 檢查並更新字典
 * 
 * @param {boolean} forceCheck - 是否強制檢查版本（忽略過期時間）
 * @returns {Promise<Object>} 字典數據對象和數據源信息
 */
export const checkAndUpdateDictionaries = async (forceCheck = false) => {
  try {
    // 檢查環境配置
    const env = checkEnvironment();
    debugLog('環境檢查', env);
    
    if (!env.configured) {
      debugLog('Supabase 未配置，使用本地字典');
      return {
        dictionaries: LOCAL_DICTIONARIES,
        dataSource: {
          type: 'local',
          configured: false,
          url: null
        }
      };
    }
    
    // 獲取本地緩存的版本號和 MD5
    const cachedVersion = await getCachedVersion();
    const cachedVersionMD5 = await getCachedVersionMD5();
    debugLog('緩存版本號', cachedVersion);
    debugLog('緩存版本 MD5', cachedVersionMD5);
    
    // 檢查是否有本地緩存
    if (cachedVersion) {
      // 檢查本地緩存的字典是否完整
      const cachedDicts = await getAllCachedDictionaries();
      const expectedDicts = ['MTR', 'LRT', 'MTRBus', 'MTRStatus', 'TrafficNews', 'Immigration'];
      const missingDicts = expectedDicts.filter(name => !cachedDicts[name]);
      
      // 如果緩存完整，檢查是否需要查詢遠程版本
      if (Object.keys(cachedDicts).length > 0 && missingDicts.length === 0) {
        // 檢查版本檢查是否仍在過期時間內
        if (!forceCheck) {
          const lastCheckTimestamp = await getLastVersionCheckTimestamp();
          const now = Date.now();
          
          if (lastCheckTimestamp && (now - lastCheckTimestamp) < VERSION_CHECK_EXPIRY_TIME) {
            // 在過期時間內，直接使用緩存，不查詢資料庫
            debugLog('版本檢查在過期時間內，使用本地緩存，跳過資料庫查詢');
            if (process.env.NODE_ENV === 'development') {
              const minutesSinceLastCheck = ((now - lastCheckTimestamp) / (60 * 1000)).toFixed(1);
              console.log(`[字典服務] ✓ 使用本地緩存（距離上次檢查 ${minutesSinceLastCheck} 分鐘）`);
            }
            
            // 驗證緩存的字典結構
            Object.keys(cachedDicts).forEach(name => {
              validateDictionaryStructure(name, cachedDicts[name]);
            });
            
            return {
              dictionaries: mergeDictionaries(cachedDicts),
              dataSource: {
                type: 'supabase',
                configured: true,
                url: env.supabaseUrl,
                version: cachedVersion,
                versionMD5: cachedVersionMD5,
                versionUpdatedAt: null,
                source: 'cache',
                skipVersionCheck: true
              }
            };
          }
        }
      }
    }
    
    // 1. 檢查 Supabase 上的版本號（只有在過期或強制檢查時才查詢）
    debugLog('檢查 Supabase 版本號...');
    let remoteVersion;
    let versionError = null;
    
    try {
      remoteVersion = await getDictionaryVersion();
      // 更新最後檢查時間戳
      await setLastVersionCheckTimestamp();
    } catch (error) {
      versionError = error.message || 'Unknown error';
      debugLog('獲取版本號時發生錯誤', error);
    }
    
    if (!remoteVersion) {
      // 無法獲取版本號，使用本地緩存或本地字典
      let errorReason;
      if (versionError) {
        // 將錯誤信息轉換為更友好的中文提示
        if (versionError.includes('網絡連接失敗') || versionError.includes('無法連接到')) {
          errorReason = '無法連接到 Supabase 服務器。請檢查網絡連接或 Supabase 配置。';
        } else if (versionError.includes('HTTP 錯誤')) {
          errorReason = `無法獲取版本號：${versionError}`;
        } else {
          errorReason = `無法獲取版本號：${versionError}`;
        }
      } else {
        errorReason = '無法獲取版本號（數據庫中可能沒有版本記錄）';
      }
      
      debugLog('無法獲取版本號，嘗試使用本地緩存', { error: versionError });
      
      // 如果有本地緩存，使用緩存
      if (cachedVersion) {
        const cachedDicts = await getAllCachedDictionaries();
        const expectedDicts = ['MTR', 'LRT', 'MTRBus', 'MTRStatus', 'TrafficNews', 'Immigration'];
        const missingDicts = expectedDicts.filter(name => !cachedDicts[name]);
        
        if (Object.keys(cachedDicts).length > 0 && missingDicts.length === 0) {
          debugLog('使用本地緩存（無法連接 Supabase）');
          return {
            dictionaries: mergeDictionaries(cachedDicts),
            dataSource: {
              type: 'supabase',
              configured: true,
              url: env.supabaseUrl,
              version: cachedVersion,
              versionMD5: cachedVersionMD5,
              versionUpdatedAt: null,
              source: 'cache',
              offline: true,
              reason: errorReason
            }
          };
        }
      }
      
      // 沒有緩存，使用本地字典
      return {
        dictionaries: LOCAL_DICTIONARIES,
        dataSource: {
          type: 'local',
          configured: true,
          url: env.supabaseUrl,
          reason: errorReason,
          error: versionError
        }
      };
    }
    
    debugLog('遠程版本號', remoteVersion);
    
    // 3. 比較版本號和 MD5
    const versionMatch = cachedVersion === remoteVersion.version;
    const md5Match = remoteVersion.versionMD5 
      ? cachedVersionMD5 === remoteVersion.versionMD5 
      : true; // 如果遠程沒有 MD5，則不檢查
    
    if (versionMatch && md5Match) {
      // 版本和 MD5 都匹配，檢查緩存
      debugLog('版本號和 MD5 匹配，檢查 IndexedDB 緩存');
      const cachedDicts = await getAllCachedDictionaries();
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[字典服務] IndexedDB 中有 ${Object.keys(cachedDicts).length} 個字典`);
        console.log(`[字典服務] 字典列表:`, Object.keys(cachedDicts));
      }
      
      // 預期的字典列表
      const expectedDicts = ['MTR', 'LRT', 'MTRBus', 'MTRStatus', 'TrafficNews', 'Immigration'];
      const missingDicts = expectedDicts.filter(name => !cachedDicts[name]);
      
      // 確保所有字典都已緩存
      if (Object.keys(cachedDicts).length > 0 && missingDicts.length === 0) {
        debugLog(`使用緩存的字典: ${Object.keys(cachedDicts).join(', ')}`);
        // 驗證緩存的字典結構
        Object.keys(cachedDicts).forEach(name => {
          validateDictionaryStructure(name, cachedDicts[name]);
        });
        return {
          dictionaries: mergeDictionaries(cachedDicts),
          dataSource: {
            type: 'supabase',
            configured: true,
            url: env.supabaseUrl,
            version: remoteVersion.version,
            versionMD5: remoteVersion.versionMD5,
            versionUpdatedAt: remoteVersion.updatedAt || null,
            source: 'cache'
          }
        };
      } else {
        if (Object.keys(cachedDicts).length === 0) {
          debugLog('IndexedDB 緩存為空，需要下載');
          if (process.env.NODE_ENV === 'development') {
            console.log('[字典服務] ⚠ IndexedDB 中沒有字典資料，即使版本號匹配，也需要下載');
          }
        } else {
          debugLog(`IndexedDB 緩存不完整，缺少字典: ${missingDicts.join(', ')}`);
          if (process.env.NODE_ENV === 'development') {
            console.log(`[字典服務] ⚠ IndexedDB 中缺少字典: ${missingDicts.join(', ')}，需要下載`);
          }
        }
      }
    } else {
      if (!versionMatch) {
        debugLog(`版本號不匹配: 緩存=${cachedVersion}, 遠程=${remoteVersion.version}`);
        if (process.env.NODE_ENV === 'development') {
          console.log(`[字典服務] 版本號不匹配，需要下載新資料`);
        }
      }
      if (remoteVersion.versionMD5 && !md5Match) {
        debugLog(`版本 MD5 不匹配: 緩存=${cachedVersionMD5}, 遠程=${remoteVersion.versionMD5}`);
        if (process.env.NODE_ENV === 'development') {
          console.log(`[字典服務] 版本 MD5 不匹配，需要下載新資料`);
        }
      }
    }
    
    // 4. 版本不同或沒有緩存，下載新數據
    debugLog('從 Supabase 下載字典數據...');
    const dictionaries = await getAllDictionaries();
    
    if (!dictionaries || Object.keys(dictionaries).length === 0) {
      // 無法獲取數據，使用本地字典
      debugLog('無法獲取字典數據，使用本地字典');
      return {
        dictionaries: LOCAL_DICTIONARIES,
        dataSource: {
          type: 'local',
          configured: true,
          url: env.supabaseUrl,
          reason: '無法獲取字典數據'
        }
      };
    }
    
    debugLog(`成功下載 ${Object.keys(dictionaries).length} 個字典`, Object.keys(dictionaries));
    
    // 驗證下載的字典結構
    Object.keys(dictionaries).forEach(name => {
      if (!validateDictionaryStructure(name, dictionaries[name])) {
        debugLog(`警告: ${name} 字典結構可能不完整`);
      }
    });
    
    // 5. 保存新數據到 IndexedDB 緩存
    if (process.env.NODE_ENV === 'development') {
      console.log('[字典服務] 開始保存字典到 IndexedDB...');
    }
    for (const dictName of Object.keys(dictionaries)) {
      try {
        const success = await setCachedDictionary(dictName, dictionaries[dictName]);
        if (success) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`[字典服務] ✓ 字典 ${dictName} 已保存到 IndexedDB`);
          }
          debugLog(`已緩存字典到 IndexedDB: ${dictName}`);
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.error(`[字典服務] ✗ 字典 ${dictName} 保存到 IndexedDB 失敗`);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error(`[字典服務] ✗ 保存字典 ${dictName} 時發生錯誤:`, error);
        }
      }
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('[字典服務] 字典保存完成');
    }
    
    // 6. 更新版本號和 MD5
    await setCachedVersion(remoteVersion.version, remoteVersion.versionMD5 || null);
    debugLog(`版本號已更新到 IndexedDB: ${remoteVersion.version}`);
    if (remoteVersion.versionMD5) {
      debugLog(`版本 MD5 已更新到 IndexedDB: ${remoteVersion.versionMD5}`);
    }
    
    debugLog('字典更新完成');
    return {
      dictionaries: mergeDictionaries(dictionaries),
      dataSource: {
        type: 'supabase',
        configured: true,
        url: env.supabaseUrl,
        version: remoteVersion.version,
        versionMD5: remoteVersion.versionMD5,
        versionUpdatedAt: remoteVersion.updatedAt || null,
        source: 'remote'
      }
    };
    
  } catch (error) {
    console.error('Failed to check and update dictionaries:', error);
    debugLog('錯誤詳情', {
      message: error.message,
      stack: error.stack
    });
    // 發生錯誤時，使用本地字典
    const env = checkEnvironment();
    return {
      dictionaries: LOCAL_DICTIONARIES,
      dataSource: {
        type: 'local',
        configured: env.configured,
        url: env.configured ? env.supabaseUrl : null,
        reason: error.message
      }
    };
  }
};

/**
 * 合併字典數據
 * 將從 Supabase 獲取的數據轉換為組件期望的格式
 * 保留本地字典中的函數（如 getWaitingStatus）
 */
const mergeDictionaries = (dictionaries) => {
  // 根據實際的 Supabase 數據結構進行轉換
  // 這裡假設 Supabase 返回的數據結構與本地字典相同
  // 但需要保留本地字典中的函數
  
  const merged = {};
  
  // 合併每個字典，保留本地字典中的函數
  Object.keys(LOCAL_DICTIONARIES).forEach(dictName => {
    const localDict = LOCAL_DICTIONARIES[dictName];
    const remoteDict = dictionaries[dictName];
    
    if (remoteDict) {
      // 如果有遠程數據，先合併數據
      merged[dictName] = { ...remoteDict };
      
      // 然後覆蓋所有函數，確保函數始終來自本地字典
      Object.keys(localDict).forEach(key => {
        if (typeof localDict[key] === 'function') {
          merged[dictName][key] = localDict[key];
        }
      });
    } else {
      // 如果沒有遠程數據，使用本地字典
      merged[dictName] = localDict;
    }
  });
  
  return merged;
};

/**
 * 強制刷新字典
 * 清除緩存並重新下載
 */
export const forceRefreshDictionaries = async () => {
  await clearAllCache();
  return await checkAndUpdateDictionaries(true); // 強制檢查
};

/**
 * 獲取字典（異步方法，使用 IndexedDB 緩存或本地字典）
 */
export const getDictionary = async (dictName) => {
  try {
    // 先嘗試從 IndexedDB 緩存獲取
    const cached = await getCachedDictionary(dictName);
    if (cached) {
      return cached;
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`從 IndexedDB 獲取字典 ${dictName} 失敗:`, error);
    }
  }
  
  // 如果沒有緩存，使用本地字典
  return LOCAL_DICTIONARIES[dictName] || null;
};
